import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Info, Lock } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/usageTracker";
import { needsCardNotPlan, planReasonOf } from "../lib/planRequired";
import { ActionButton, Field } from "../components/kit";
import LoadFailureNote from "../components/LoadFailureNote";
import PayChrome, { WithTokens, PlaceholderToken } from "../components/PayChrome";
import {
  ALREADY_SUBSCRIBED,
  APP_KEEPS,
  CARD_NEEDED_GUARDIAN_LINE,
  CARD_NEEDED_LINE,
  CARD_NEEDED_LINK,
  CARD_NEEDED_TITLE,
  CHARGES_LABEL,
  CHARGES_LINES,
  CHECKOUT_COULD_NOT_OPEN,
  CHOOSE_LEDE_BOTH,
  CHOOSE_REASON,
  CHOOSE_LEDE_COACH,
  CHOOSE_LEDE_PLAYER,
  CHOOSE_TITLE,
  CONTINUE_LABEL,
  DOB_LOCK,
  DOB_NEEDED,
  FLOW_CHOOSE,
  FLOW_CURRENT,
  GUARDIAN_HELP,
  GUARDIAN_HOLDS_CARD,
  GUARDIAN_INVALID,
  GUARDIAN_LABEL,
  GUARDIAN_REQUIRED,
  NO_REFUND_ANNUAL,
  OPENING_STRIPE,
  OPEN_SETTINGS,
  PAY_LABEL,
  PER_MONTH,
  PER_YEAR,
  PERIOD_ANNUAL,
  PERIOD_MONTHLY,
  PLANS_FAILED_WHAT,
  PLAN_NAME,
  PLAN_NOTE_BOTH,
  BOTH_ROLES_LINE,
  CARD_FEE_LINE,
  TOTAL_ANNUAL,
  TOTAL_MONTHLY,
  YOUR_PLAN,
  RECEIPTS_LINE,
  STRIPE_HOLDS,
  STRIPE_LABEL,
  TEN_PERCENT_OFF,
  TOKEN,
  formatMoney,
  looksLikeEmail,
  merchantLine,
  planFromCodes,
  priceFor,
  priceWithFee,
  pricesIn,
} from "../lib/payCopy";

/**
 * Choose your plan (`/plan`, #1036, D204.2), behind `newUi("pay")`.
 *
 * The prices are Stripe's, read from `GET /api/billing/plans`; the page never
 * holds a figure. `[PRICE]` and `[CURRENCY]` are drawn only when that answers
 * 503 `billing_unavailable`. Which plan is shown is decided by the codes the
 * API sends for this account: all three means the account holds both roles.
 *
 * States: `plans-loading`, `plans-failed-to-load` (with a retry), the plan
 * with monthly ticked, `annual-selected`, `player-11-to-17` (the guardian
 * email is required), `opening-stripe` (the gold reads the busy label and is
 * inert), `checkout-could-not-open`. Every failure is on the page (D57, D55);
 * there is no toast. One gold element: Continue to payment (R-003).
 */
const card = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 14,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: 12,
};
const eyebrow = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--nw-ink-muted)",
};

function Note({ testId, children, alert = false }) {
  return (
    <div
      data-testid={testId}
      role={alert ? "alert" : undefined}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "var(--nw-line-faint)",
        borderLeft: alert ? "3px solid var(--nw-alert)" : undefined,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--nw-ink)",
      }}
    >
      {alert && <Info size={16} color="#B42318" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />}
      <span>{children}</span>
    </div>
  );
}

function PeriodChoice({ id, selected, onSelect, label, sub, disabled }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={id}
      data-fill={selected ? "navy" : undefined}
      disabled={disabled}
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minHeight: 48,
        padding: "0 12px",
        textAlign: "left",
        background: selected ? "var(--nw-navy)" : "var(--nw-surface)",
        border: selected ? "2px solid var(--nw-navy)" : "1px solid var(--nw-line)",
        borderRadius: 10,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 18,
          height: 18,
          flexShrink: 0,
          borderRadius: 999,
          border: `2px solid ${selected ? "#FFFFFF" : "var(--nw-line-strong)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {selected && <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FFFFFF" }} />}
      </span>
      <span style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 14, fontWeight: selected ? 700 : 600, color: selected ? "#FFFFFF" : "var(--nw-ink)" }}>{label}</span>
        {sub && <span style={{ fontSize: 11, fontWeight: 600, color: selected ? "#FFFFFF" : "var(--nw-ink-muted)" }}>{sub}</span>}
      </span>
    </button>
  );
}

function Money({ price, unavailable }) {
  if (price) {
    const text = formatMoney(price.unit_amount, price.currency);
    if (text) return <span data-testid="plan-price">{text}</span>;
  }
  return unavailable ? <PlaceholderToken>{TOKEN.PRICE}</PlaceholderToken> : <span aria-hidden="true">·</span>;
}

function FeeLine({ price, period }) {
  const parts = priceWithFee(price);
  if (!parts) return null;
  return (
    <span data-testid={`plan-fee-${period}`} style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>
      {CARD_FEE_LINE(parts.fee)}
    </span>
  );
}

function PlanBlock({ code, plans, unavailable, period }) {
  const monthly = priceFor(plans, code, "monthly");
  const annual = priceFor(plans, code, "annual");
  const chosen = priceWithFee(period === "annual" ? annual : monthly);
  return (
    <div
      data-testid={`plan-card-${code}`}
      style={{ ...card, gap: 0, padding: 0, overflow: "hidden", border: "2px solid var(--nw-navy)" }}
    >
      <div
        data-testid="plan-header"
        data-fill="navy"
        style={{ display: "flex", flexDirection: "column", gap: 2, padding: "12px 14px", background: "var(--nw-navy)", color: "#FFFFFF" }}
      >
        <span style={{ ...eyebrow, color: "#FFFFFF" }}>{YOUR_PLAN}</span>
        <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18, color: "#FFFFFF" }}>{PLAN_NAME[code]}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 14, background: "var(--nw-surface)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          <div style={{ ...card, padding: "8px 12px", gap: 2 }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-ink)" }}>
              <Money price={monthly} unavailable={unavailable} />
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>{PER_MONTH}</span>
            <FeeLine price={monthly} period="monthly" />
          </div>
          <div style={{ ...card, padding: "8px 12px", gap: 2 }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-ink)" }}>
              <Money price={annual} unavailable={unavailable} />
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>{PER_YEAR}</span>
            <FeeLine price={annual} period="annual" />
          </div>
        </div>
        {chosen && (
          <div data-testid="plan-total" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-ink)" }}>
            {period === "annual" ? TOTAL_ANNUAL(chosen.total) : TOTAL_MONTHLY(chosen.total)}
          </div>
        )}
        {code === "both" && <div style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>{PLAN_NOTE_BOTH}</div>}
      </div>
    </div>
  );
}

export default function ChoosePlanPage() {
  // load: "loading" | "ready" | "unavailable" (503) | "failed"
  const [load, setLoad] = useState("loading");
  const [plans, setPlans] = useState([]);
  const [facts, setFacts] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const reason = planReasonOf(useLocation().search);
  const [period, setPeriod] = useState("monthly");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianError, setGuardianError] = useState("");
  const [opening, setOpening] = useState(false);
  const [failure, setFailure] = useState(null); // null | "could-not-open" | "already" | "dob"
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    track("plan.reached");
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadAll = useCallback(async () => {
    setLoad("loading");
    const [plansRes, meRes, statusRes] = await Promise.allSettled([api.get("/billing/plans"), api.get("/auth/me"), api.get("/billing/status")]);
    if (!mounted.current) return;
    if (meRes.status === "fulfilled") setFacts(meRes.value.data);
    // #1671: a failed status read leaves the picker, as before; only a clear "payment failed" answer replaces it.
    setSubscription(statusRes.status === "fulfilled" ? statusRes.value.data : null);
    if (plansRes.status === "fulfilled" && meRes.status === "fulfilled") {
      setPlans(Array.isArray(plansRes.value.data?.plans) ? plansRes.value.data.plans : []);
      setLoad("ready");
    } else if (plansRes.status === "rejected" && plansRes.reason?.response?.status === 503) {
      setPlans([]);
      setLoad(meRes.status === "fulfilled" ? "unavailable" : "failed");
    } else {
      setLoad("failed");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const plan = useMemo(() => {
    if (load === "ready") return planFromCodes(plans.map((p) => p.code));
    if (load === "unavailable") {
      const roles = new Set(facts?.roles || [facts?.role]);
      if (roles.has("player") && roles.has("coach")) return "both";
      return roles.has("coach") ? "coach" : "player";
    }
    return null;
  }, [load, plans, facts]);

  const needsGuardian = facts?.age_band === "11_to_17";
  const cardNeeded = load !== "loading" && load !== "failed" && needsCardNotPlan(subscription);
  const lede = plan === "both" ? CHOOSE_LEDE_BOTH : plan === "coach" ? CHOOSE_LEDE_COACH : CHOOSE_LEDE_PLAYER;
  const unavailable = load === "unavailable";
  const currencyRow = plans[0]?.currency ? plans[0].currency.toUpperCase() : null;
  const taxWording = plans.find((p) => p.tax_wording)?.tax_wording || null;

  const checkout = async () => {
    if (opening) return;
    setFailure(null);
    setGuardianError("");
    if (needsGuardian) {
      const v = guardianEmail.trim();
      if (!v) return setGuardianError(GUARDIAN_REQUIRED);
      if (!looksLikeEmail(v)) return setGuardianError(GUARDIAN_INVALID);
    }
    setOpening(true);
    try {
      const body = { period };
      if (needsGuardian) body.payer_email = guardianEmail.trim();
      const { data } = await api.post("/billing/checkout", body);
      if (typeof data?.url === "string" && data.url) {
        track("plan.checkout_started");
        window.location.assign(data.url);
        return; // leaving the page: the gold stays busy
      }
      throw new Error("no url");
    } catch (e) {
      if (!mounted.current) return;
      const code = e?.response?.data?.code;
      if (e?.response?.status === 409 && code === "already_subscribed") setFailure("already");
      else if (code === "dob_required") setFailure("dob");
      else if (code === "payer_email_required") setGuardianError(GUARDIAN_REQUIRED);
      else setFailure("could-not-open");
      setOpening(false);
    }
  };

  const body = () => {
    if (cardNeeded) {
      return (
        <div style={{ ...card, borderLeft: "3px solid var(--nw-navy)" }} data-testid="plan-card-needed">
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}>{needsGuardian ? CARD_NEEDED_GUARDIAN_LINE : CARD_NEEDED_LINE}</div>
          <Link
            to="/settings"
            data-testid="plan-card-needed-settings"
            style={{ display: "inline-flex", alignItems: "center", minHeight: 44, fontSize: 14, fontWeight: 700, color: "var(--nw-navy)" }}
          >
            {CARD_NEEDED_LINK}
          </Link>
        </div>
      );
    }
    if (load === "loading") {
      return (
        <div data-testid="plans-loading" aria-busy="true" aria-label="Loading the plans" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[96, 140, 120].map((h) => (
            <div key={h} style={{ height: h, borderRadius: 12, background: "var(--nw-line-faint)" }} />
          ))}
        </div>
      );
    }
    if (load === "failed") {
      return <LoadFailureNote what={PLANS_FAILED_WHAT} onRetry={loadAll} testId="plans-failed-to-load" tapBox />;
    }
    return (
      <>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }} id="plan-pay-label">
            {PAY_LABEL}
          </div>
          <div role="radiogroup" aria-labelledby="plan-pay-label" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
            <PeriodChoice id="plan-period-monthly" selected={period === "monthly"} onSelect={() => setPeriod("monthly")} label={PERIOD_MONTHLY} disabled={opening} />
            <PeriodChoice id="plan-period-annual" selected={period === "annual"} onSelect={() => setPeriod("annual")} label={PERIOD_ANNUAL} sub={TEN_PERCENT_OFF} disabled={opening} />
          </div>
        </div>

        {plan && <PlanBlock code={plan} plans={plans} unavailable={unavailable} period={period} />}
        {plan === "both" && (
          <div data-testid="plan-both-line" style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
            {BOTH_ROLES_LINE}
          </div>
        )}

        <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
          {unavailable ? <WithTokens text={pricesIn(TOKEN.CURRENCY)} /> : currencyRow ? pricesIn(currencyRow) : null}
          {unavailable ? (
            <>
              {" "}
              <PlaceholderToken>{TOKEN.TAX_WORDING}</PlaceholderToken>
            </>
          ) : taxWording ? (
            <> {taxWording}</>
          ) : null}
        </div>

        <div style={{ ...card, borderLeft: "3px solid var(--nw-navy)" }} data-testid="plan-charges">
          <div style={eyebrow}>{CHARGES_LABEL}</div>
          <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {CHARGES_LINES.map((line, i) => (
              <li key={line} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
                <span
                  aria-hidden="true"
                  style={{ minWidth: 18, height: 18, borderRadius: 999, background: "#E8F1FB", fontSize: 11, fontWeight: 700, color: "var(--nw-navy)", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}
                >
                  {i + 1}
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{GUARDIAN_HOLDS_CARD}</div>
          {period === "annual" && (
            <div data-testid="plan-no-refund" style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink)" }}>
              {NO_REFUND_ANNUAL}
            </div>
          )}
        </div>

        {needsGuardian && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="plan-guardian">
            <Field
              label={GUARDIAN_LABEL}
              type="email"
              autoComplete="off"
              inputMode="email"
              value={guardianEmail}
              onChange={(e) => {
                setGuardianEmail(e.target.value);
                if (guardianError) setGuardianError("");
              }}
              readOnly={opening}
              required
              error={guardianError || null}
              help={guardianError ? null : GUARDIAN_HELP}
              data-testid="plan-guardian-email"
            />
            <div data-testid="plan-dob-lock" style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: "var(--nw-ink-muted)" }}>
              <Lock size={14} aria-hidden="true" />
              <span>{DOB_LOCK}</span>
            </div>
          </div>
        )}

        <div style={card} data-testid="plan-stripe">
          <div style={eyebrow}>{STRIPE_LABEL}</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{STRIPE_HOLDS}</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{APP_KEEPS}</div>
          <div data-testid="plan-receipts" style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
            {RECEIPTS_LINE}
          </div>
        </div>

        {failure === "could-not-open" && (
          <Note testId="checkout-could-not-open" alert>
            {CHECKOUT_COULD_NOT_OPEN}
          </Note>
        )}
        {failure === "already" && (
          <Note testId="plan-already-subscribed" alert>
            {ALREADY_SUBSCRIBED} <Link to="/settings">{OPEN_SETTINGS}</Link>
          </Note>
        )}
        {failure === "dob" && (
          <Note testId="plan-dob-needed" alert>
            {DOB_NEEDED} <Link to="/settings">{OPEN_SETTINGS}</Link>
          </Note>
        )}

        <ActionButton
          data-testid="plan-checkout"
          onClick={checkout}
          aria-disabled={opening || undefined}
          style={opening ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
        >
          {opening ? <span data-testid="opening-stripe">{OPENING_STRIPE}</span> : CONTINUE_LABEL}
        </ActionButton>

        <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
          <WithTokens text={merchantLine()} />
        </div>
      </>
    );
  };

  return (
    <PayChrome screenTestId="choose-plan-new-screen" flow={FLOW_CHOOSE} current={FLOW_CURRENT.choose} flowTestId="choose-plan-flow" inert={opening}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1 className="nw-title-lg" style={{ margin: 0 }} data-testid="choose-plan-title">
          {cardNeeded ? CARD_NEEDED_TITLE : CHOOSE_TITLE}
        </h1>
        {reason && !cardNeeded && (
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.5, color: "var(--nw-ink)" }} data-testid="choose-plan-reason">
            {CHOOSE_REASON}
          </p>
        )}
        {load !== "loading" && load !== "failed" && !cardNeeded && (
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="choose-plan-lede">
            {lede}
          </p>
        )}
      </div>
      {body()}
    </PayChrome>
  );
}
